import { gql } from "@apollo/client";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { format } from "date-fns";
import { capitalize } from "lodash";
import { useState } from "react";

import CopyButton from "~/components/common/CopyButton";
import DownloadButton from "~/components/common/DownloadButton";
import { ApiKeysQuery, useWebhookPublicKeyLazyQuery } from "~/graphql/__generated__/graphql";
import DeleteKeyDialog from "./DeleteKeyDialog";
import CreateApiKey from "./CreateApiKey";

type Props = {
  apiKeys: ApiKeysQuery["apiKey"]
}

const ApiKeys = ({ apiKeys }: Props) => {
  const [keyToDelete, setKeyToDelete] = useState<ApiKeysQuery["apiKey"][number] | null>(null);
  const [] = useState<ApiKeysQuery["apiKey"][number] | null>(null);

  const [getPublicKey] = useWebhookPublicKeyLazyQuery();

  return <>
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>API Key</TableCell>
            <TableCell>Purpose</TableCell>
            <TableCell>Acting As</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell>Expires</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apiKeys.map((key) => (
            <TableRow key={key.id}>
              <TableCell>
                {key.apiKey}
                <Tooltip title="Copy key ID to clipboard">
                  <CopyButton text={key.apiKey} />
                </Tooltip>
              </TableCell>
              <TableCell>
                {key.keyPurpose === 'sftp' ? `SFTP (${key.algorithm.split('_')[1]})` : capitalize(key.keyPurpose)}

                {key.keyPurpose === "webhook" && <Tooltip title="Download public key">
                  <DownloadButton fetch={async () => {
                    const { data } = await getPublicKey({ variables: { apiKeyId: key.id }});
                    if (!data) { return; }
                    return {
                      fileType: "application/x-pem-file",
                      fileName: `medarrive-webhook-${key.apiKey}.pem`,
                      text: data.webhookPublicKey.publicPem,
                    }
                  }} />
                </Tooltip>}
              </TableCell>
              <TableCell>
                {key.organizationUser?.user?.firstName} {key.organizationUser?.user?.lastName}
              </TableCell>
              <TableCell>
                {format(new Date(key.createdAt), "MMM d, yyyy h:MM a")}
              </TableCell>
              <TableCell>
                {key.createdByUser?.firstName} {key.createdByUser?.lastName}
              </TableCell>
              <TableCell>
                {key.expiresAt
                  ? format(new Date(key.expiresAt), "MMM d, yyyy h:MM a")
                  : "Never"}
              </TableCell>
              <TableCell>
                <Button color="error" onClick={() => setKeyToDelete(key)}>
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3}>
              <CreateApiKey />
            </TableCell>
            </TableRow>
        </TableBody>
      </Table>
    </TableContainer>

    {keyToDelete && <DeleteKeyDialog keyToDelete={keyToDelete} onClose={() => setKeyToDelete(null)} />}
  </>
}

export default ApiKeys;

gql`
  query WebhookPublicKey($apiKeyId: Int!)
  @permission(requires: manageApiKeys) {
    webhookPublicKey(apiKeyId: $apiKeyId) {
      id
      publicPem
    }
  }
`;
